import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal, ImageReveal } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { InstagramFeed } from "../components/InstagramFeed";
import { IMAGES, SERVICES, STATS, PORTFOLIO } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.3, 0.8]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={IMAGES.hero}
          data-testid="hero-video"
        >
          <source src={IMAGES.heroVideo} type="video/mp4" />
        </video>
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#0D0B0A]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0A] via-[#0D0B0A]/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0B0A]/70 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-24 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline mb-8 text-sand-700"
        >
          Desert Vibe Media — Video Production
        </motion.p>
        <h1 className="font-display text-[15vw] leading-[0.85] tracking-tight text-sand-50 text-shadow-cinematic md:text-[9rem]">
          <MaskLines lines={["Your story is", <em key="e" className="italic text-sand-700">our business.</em>]} delay={0.35} />
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between"
        >
          <p className="max-w-md font-body text-lg font-normal text-sand-100 text-shadow-cinematic">
            A desert-born film studio crafting cinematic brand stories, commercials, and documentaries.
          </p>
          <Link to="/portfolio">
            <span className="btn-desert" data-testid="hero-cta">
              View the showreel <ArrowUpRight size={16} />
            </span>
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-sand-400 md:flex lg:right-10"
      >
        <ArrowDown size={16} className="animate-bounce" /> <span className="overline">Scroll</span>
      </motion.div>
    </section>
  );
};

const Stats = () => (
  <section className="border-b border-border bg-surface" data-testid="stats">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px md:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="px-6 py-14 lg:px-10">
          <p className="font-display text-6xl text-sand-700">{s.value}</p>
          <p className="overline mt-3 text-sand-400">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Intro = () => (
  <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="intro">
    <div className="grid gap-16 md:grid-cols-12">
      <div className="md:col-span-4">
        <p className="overline text-sand-700">(Who we are)</p>
      </div>
      <div className="md:col-span-8">
        <Reveal>
          <p className="font-display text-3xl leading-snug text-sand-100 md:text-5xl md:leading-[1.15]">
            We are storytellers of the arid frontier — pairing the patience of the
            <span className="italic text-sand-700"> owl </span>
            with the precision of the
            <span className="italic text-sand-700"> hawk </span>
            to turn brands into films worth remembering.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="services-preview">
    <Reveal className="mb-16 flex items-end justify-between">
      <h2 className="font-display text-5xl text-sand-100 md:text-7xl">What we craft</h2>
      <Link to="/services" className="hidden md:inline-flex">
        <span className="overline link-underline text-sand-400 hover:text-sand-100">All services</span>
      </Link>
    </Reveal>
    <div className="grid gap-px border border-border md:grid-cols-3">
      {SERVICES.slice(0, 6).map((s, i) => (
        <Reveal key={s.no} delay={(i % 3) * 0.08}>
          <div className="group h-full bg-surface p-10 transition-colors duration-500 hover:bg-[#1d1815]">
            <span className="font-display text-2xl italic text-sand-700">{s.no}</span>
            <h3 className="mt-6 font-display text-3xl text-sand-100">{s.title}</h3>
            <p className="mt-4 font-body text-sm font-light leading-relaxed text-sand-400">{s.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const SunsetBanner = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[80vh] w-full overflow-hidden" data-testid="sunset-banner">
      <motion.div style={{ y, scale: 1.15 }} className="absolute inset-0">
        <img src={IMAGES.sunset} alt="Desert sunset over silhouetted mountains" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0B0A] via-transparent to-[#0D0B0A]" />
      <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <p className="overline text-sand-50 text-shadow-cinematic">Golden hour, every hour</p>
          <p className="mt-8 font-display text-4xl italic leading-tight text-sand-50 text-shadow-cinematic md:text-6xl md:leading-[1.1]">
            &ldquo;We chase the light the desert gives once a day — and hold it on screen forever.&rdquo;
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const WorkPreview = () => (
  <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="work-preview">
    <Reveal className="mb-16">
      <p className="overline text-sand-700">Selected Work</p>
      <h2 className="mt-6 font-display text-5xl text-sand-100 md:text-7xl">Recent frames</h2>
    </Reveal>
    <div className="grid gap-8 md:grid-cols-2">
      {PORTFOLIO.slice(0, 2).map((p, i) => (
        <Reveal key={p.id} delay={i * 0.1}>
          <Link to="/portfolio" className="group block">
            <ImageReveal
              src={p.thumb}
              alt={p.title}
              className={i === 1 ? "aspect-[4/5]" : "aspect-[4/5] md:mt-24"}
              imgClassName="transition-transform duration-700 group-hover:scale-105"
            />
            <div className="mt-5 flex items-center justify-between">
              <span className="font-display text-3xl text-sand-100">{p.title}</span>
              <span className="overline text-sand-400">{p.category}</span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-16 text-center">
      <Link to="/portfolio"><span className="btn-desert" data-testid="work-preview-cta">Explore the archive <ArrowUpRight size={16} /></span></Link>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Stats />
      <Intro />
      <EditorialMarquee />
      <ServicesPreview />
      <SunsetBanner />
      <WorkPreview />
      <InstagramFeed />
    </div>
  );
}
